import React, { useEffect, useRef } from 'react';
import './About.css';

/* ── Inline SectionLabel ── */
const SectionLabel: React.FC<{ text: string }> = ({ text }) => (
  <div className="section-label">
    <span>{text}</span>
  </div>
);

/* ── SVG Icons for bullets ── */
const IconTeam = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B8DEF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const IconShield = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B8DEF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const IconChart = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B8DEF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6"  y1="20" x2="6"  y2="14"/>
    <line x1="2"  y1="20" x2="22" y2="20"/>
  </svg>
);

/* ── Data ── */
const bullets = [
  { label: 'Engineers & industry veterans', Icon: IconTeam },
  { label: 'Ethical & responsible AI',      Icon: IconShield },
  { label: 'Scalable solutions across sectors', Icon: IconChart },
];

/* ── Component ── */
const About: React.FC = () => {
  const layoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (layoutRef.current) obs.observe(layoutRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="about" id="about">
      <div className="about__content">

        {/* Section heading */}
        <SectionLabel text="Who We Are" />

        {/* ── Two-column card ── */}
        <div className="about__layout reveal" ref={layoutRef}>

          {/* Col 1 — Navy panel */}
          <div className="about__col about__col--statement">
            <div className="about__big">
              We build<br />
              AI that<br />
              <em>works.</em>
            </div>

            <div className="about__col-footer">
              <span className="about__badge">Est. 2021 · Hyderabad</span>
              {bullets.map(({ label, Icon }) => (
                <div className="about__bullet" key={label}>
                  <div className="about__bullet-icon">
                    <Icon />
                  </div>
                  <span className="about__bullet-text">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Col 2 — Body copy + pull-quote */}
          <div className="about__col about__col--text">
            <div className="about__text-body">
              <p className="about__para">
                Rapidus AI Private Limited is a forward-thinking technology company
                dedicated to building intelligent, ethical, and scalable AI solutions.
              </p>
              <p className="about__para">
                Founded by engineers and industry veterans, we transform industries and
                empower businesses by harnessing the full potential of artificial intelligence.
              </p>
              <p className="about__para">
                We design tailor-made solutions for diverse industries — spanning IT, CRM,
                employee experience, data analytics, healthcare, finance, logistics, and retail —
                helping businesses execute workflows faster and smarter.
              </p>
            </div>

            <div className="about__pull-quote-wrapper">
              <div className="about__quote-icon">
                <svg width="28" height="22" viewBox="0 0 28 22" fill="#1A50C8" opacity="0.18">
                  <path d="M0 22V13.5C0 9.833 1.083 6.833 3.25 4.5 5.417 2.167 8.333.667 12 0l1.5 2.5C10.167 3.333 8.167 4.583 7 6.25 5.833 7.917 5.333 9.833 5.5 12H11V22H0zm16 0V13.5c0-3.667 1.083-6.667 3.25-9C21.417 2.167 24.333.667 28 0l1.5 2.5c-3.333.833-5.333 2.083-6.5 3.75C21.833 7.917 21.333 9.833 21.5 12H27V22H16z"/>
                </svg>
              </div>
              <div className="about__pull-quote">
                <p>
                  "Our mission is to deliver reliable AI technologies that help businesses
                  automate processes, gain insights, and create lasting value."
                </p>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default About;